/**
 * Visitor attribution capture (browser side).
 *
 * This file is deliberately self-contained — no imports — because an identical
 * copy lives in every browser bundle:
 *
 *   apps/widget/src/lib/attribution.ts
 *   apps/widget_v2/src/lib/attribution.ts
 *   apps/form-api/src/attribution.ts
 *
 * Keep the three copies in sync. If you change one, change all three.
 *
 * ---------------------------------------------------------------------------
 * Division of labour: THE BROWSER CAPTURES RAW SIGNALS, THE BACKEND INTERPRETS.
 *
 * These bundles are served from a CDN and embedded on customer sites, so a fix
 * shipped here can take weeks to reach every visitor. The backend deploys in
 * minutes. So this file does the one thing only the browser can do — read the
 * URL, the referrer and the GA cookies before they evaporate — and sends them
 * up verbatim. All the judgement (is this paid? which channel bucket?) lives in
 * apps/backend/utils/attribution.js, where it can be corrected at any time.
 *
 * Do not add source/medium classification here.
 * ---------------------------------------------------------------------------
 *
 * Iframe embedding: when the widget runs inside an iframe, `window.location` is
 * the widget's own URL and says nothing about the visitor. Three views of the
 * host page are tried, best first — see `readPageContext`. The best of them
 * needs the embedding page to hand its URL over, which is why this file speaks a
 * small postMessage handshake: it asks, and uses the answer if one comes.
 *
 * Two touches are kept:
 *   first — the visit that originally introduced this person. localStorage,
 *           90 days, written once and never overwritten.
 *   last  — the visit that led to this conversion. sessionStorage, so it
 *           naturally resets per session the way GA4 counts sessions.
 */

const FIRST_KEY = 'zentri:attr:first';
const LAST_KEY = 'zentri:attr:last';

/** First touch outlives the session: 90 days, matching GA4's default lookback. */
const FIRST_TOUCH_TTL_MS = 90 * 24 * 60 * 60 * 1000;

/** Long enough for real campaign URLs, short enough not to bloat a request. */
const MAX_VALUE = 1024;

/** Caps on the parameter bag, so a hostile or broken URL cannot bloat a request. */
const MAX_PARAMS = 50;
const MAX_PARAM_KEY = 64;
const MAX_PARAM_VALUE = 256;

/**
 * Which query keys mark a genuinely new origin.
 *
 * This is the ONLY judgement left in the browser, and it deliberately decides
 * something small: whether this page view is worth overwriting the stored "last
 * touch" with. It never decides a source, a medium or a channel — those are the
 * backend's, from the raw parameters we send up regardless.
 *
 * Getting it wrong is therefore cheap and self-correcting: an unrecognised marker
 * just means the previous touch is kept, and the full URL still reaches the
 * backend on the next page view that does match. That is why a stale pattern list
 * here is tolerable while a stale classifier here would not be.
 *
 * Written as shapes rather than a list of names so new ad networks are usually
 * covered on the day they launch: every major network's click id ends in some
 * spelling of "click id".
 */
const ORIGIN_MARKER =
    /^(utm_|gad_)|(clid|clkid|clickid|click_id|_cid)$|^(gbraid|wbraid|li_fat_id|srsltid|epik|sccid)$/;

/**
 * Keys never worth sending, as a first pass only. Some sites reflect submitted
 * values back into the URL, and that data has no business leaving the page.
 * The backend applies the authoritative, much stricter filter before anything is
 * stored — this exists so the data is not transmitted in the first place.
 */
const SENSITIVE_KEY = /(e?mail|phone|tel(ephone)?|name|password|passwd|pwd|token|auth|secret|apikey|api_key|ssn|dob|card|cvv|iban)/;

/**
 * Raw evidence exactly as the browser found it. No interpretation.
 *
 * Every query parameter goes into `params` untouched — we no longer keep a
 * whitelist of "interesting" keys, because every whitelist we have written has
 * eventually lost a parameter a customer cared about, and a parameter not
 * captured at the moment of the visit is unrecoverable. The structural fields
 * stay outside the bag so a query parameter that happens to be called
 * "referrer" cannot overwrite the real one.
 */
export interface RawTouch {
    params?: Record<string, string>;
    referrer?: string;
    landing_page?: string;
    captured_at?: string;
    /**
     * Internal only, stripped before anything is sent. Marks a touch captured
     * inside an iframe before the embedding page answered with its real URL, so
     * that one — and only that one — may be replaced when the answer arrives.
     */
    provisional?: boolean;
}

export interface AttributionPayload {
    first: RawTouch | null;
    last: RawTouch | null;
    ga_client_id?: string | null;
    ga_session_id?: string | null;
}

// --- storage -----------------------------------------------------------------
// Wrapped in try/catch throughout: storage throws in private mode, in sandboxed
// iframes, and when a site has blocked third-party storage. Attribution is never
// worth breaking a chat widget over, so every failure degrades to "no data".

interface Stored<T> {
    data: T;
    expiresAt: number;
}

function readStore(session: boolean): Storage | null {
    try {
        if (typeof window === 'undefined') return null;
        return session ? window.sessionStorage : window.localStorage;
    } catch {
        return null;
    }
}

function readTouch(key: string, session: boolean): RawTouch | null {
    try {
        const store = readStore(session);
        if (!store) return null;
        const raw = store.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Stored<RawTouch>;
        if (!parsed || typeof parsed !== 'object' || !parsed.data) return null;
        if (typeof parsed.expiresAt === 'number' && Date.now() > parsed.expiresAt) {
            try { store.removeItem(key); } catch { /* ignore */ }
            return null;
        }
        return parsed.data;
    } catch {
        return null;
    }
}

function writeTouch(key: string, touch: RawTouch, session: boolean, ttlMs: number): void {
    try {
        const store = readStore(session);
        if (!store) return;
        const payload: Stored<RawTouch> = { data: touch, expiresAt: Date.now() + ttlMs };
        store.setItem(key, JSON.stringify(payload));
    } catch {
        // ignore — quota, private mode, blocked storage
    }
}

// --- reading the page --------------------------------------------------------

function clean(value: string | null | undefined): string | undefined {
    if (typeof value !== 'string') return undefined;
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    return trimmed.length > MAX_VALUE ? trimmed.slice(0, MAX_VALUE) : trimmed;
}

/** The query string of a URL, without the leading `?` and without any `#fragment`. */
function queryOf(url: string | undefined): string {
    if (!url) return '';
    const start = url.indexOf('?');
    if (start < 0) return '';
    const rest = url.slice(start + 1);
    const hash = rest.indexOf('#');
    return hash < 0 ? rest : rest.slice(0, hash);
}

/** True when this bundle is running inside an iframe rather than the page itself. */
function inIframe(): boolean {
    try {
        return window.self !== window.top;
    } catch {
        return true; // cross-origin parent access throws → definitely framed
    }
}

/** The host page's own view of itself, once it has told us. See the handshake below. */
let hostContext: PageContext | null = null;

interface PageContext {
    pageUrl?: string;
    referrer?: string;
    /** True when this is a guess made from inside the frame rather than the host's own answer. */
    provisional?: boolean;
}

/**
 * Which URL the visitor is actually on, and where they came from.
 *
 * Three views of the host page, best first:
 *
 *  1. What the embedding page told us, via the handshake below. The only view
 *     that includes the host's real referrer, so it is the only one that can
 *     tell a framed visitor from Google apart from a framed direct visitor.
 *  2. Reading the parent directly. Works when the embed is same-origin (an
 *     `about:blank` iframe written by the host, which is how our own embed
 *     works) and throws otherwise, which is exactly the right test.
 *  3. `document.referrer`, which inside a frame is the embedding page. Then the
 *     referrer IS the landing page — and must not be reported as a referrer, or
 *     every framed lead would be classified as a referral from the customer's
 *     own site. The visitor's true referrer is not visible from in here, so a
 *     touch captured this way is marked provisional and may be replaced if the
 *     host answers.
 */
function readPageContext(): PageContext {
    try {
        if (!inIframe()) {
            return { pageUrl: clean(window.location.href), referrer: clean(document.referrer) };
        }

        if (hostContext) return hostContext;

        try {
            const parent = window.parent;
            const href = clean(parent.location.href);
            if (href) return { pageUrl: href, referrer: clean(parent.document.referrer) };
        } catch {
            // cross-origin parent — expected, fall through
        }

        return { pageUrl: clean(document.referrer), provisional: true };
    } catch {
        return {};
    }
}

// --- host page handshake -----------------------------------------------------

/** Sent to the embedding page on load; answered with PAGE_CONTEXT_REPLY. */
const PAGE_CONTEXT_REQUEST = 'zentri:request-page-context';
const PAGE_CONTEXT_REPLY = 'zentri:page-context';

let handshakeStarted = false;

/**
 * Ask the embedding page where the visitor actually is.
 *
 * Only the page itself can answer this: a cross-origin frame cannot read the
 * parent's URL, and `document.referrer` gives at best the address without the
 * campaign tags — under a strict Referrer-Policy, only the origin. So the widget
 * asks, and if an answer comes back it re-runs capture with it.
 *
 * The answer is accepted only from our own parent frame. The origin is not
 * checked because the host's domain is by definition the customer's and unknown
 * to us here; what arrives is treated as untrusted text either way — it is
 * capped, and every judgement about what it means is the backend's.
 *
 * A host that never answers costs nothing: the provisional touch already
 * captured stands.
 */
function requestHostContext(): void {
    if (handshakeStarted) return;
    handshakeStarted = true;

    try {
        window.addEventListener('message', (event: MessageEvent) => {
            try {
                if (event.source !== window.parent) return;
                const data = event.data as { type?: string; href?: string; referrer?: string } | null;
                if (!data || data.type !== PAGE_CONTEXT_REPLY) return;

                const pageUrl = clean(data.href);
                if (!pageUrl) return;

                hostContext = { pageUrl, referrer: clean(data.referrer) };
                // Re-run now that we know where the visitor really is. This is
                // what replaces the provisional touch written moments ago.
                captureAttribution();
            } catch {
                // ignore — a malformed message from the page is not our problem
            }
        });

        window.parent.postMessage({ type: PAGE_CONTEXT_REQUEST }, '*');
    } catch {
        // ignore — sandboxed frame, no parent access
    }
}

/**
 * Build a touch from the page as it is right now. Query keys are lowercased
 * because real campaign URLs arrive with `UTM_Source` and worse.
 */
function readCurrentTouch(): RawTouch {
    const touch: RawTouch = {};
    const { pageUrl, referrer, provisional } = readPageContext();
    const bag: Record<string, string> = {};

    try {
        // Parsed from the landing URL rather than window.location.search, so the
        // framed case reads the host page's campaign tags and not the iframe's.
        const params = new URLSearchParams(queryOf(pageUrl));
        let kept = 0;
        params.forEach((value, key) => {
            if (kept >= MAX_PARAMS) return;
            const name = key.trim().toLowerCase();
            if (!name || name.length > MAX_PARAM_KEY) return;
            // First occurrence wins; a repeated key is a malformed URL.
            if (name in bag) return;
            if (SENSITIVE_KEY.test(name)) return;
            const cleaned = clean(value);
            if (!cleaned) return;
            if (cleaned.indexOf('@') >= 0) return; // looks like an address, not a tag
            bag[name] = cleaned.length > MAX_PARAM_VALUE ? cleaned.slice(0, MAX_PARAM_VALUE) : cleaned;
            kept += 1;
        });
    } catch {
        // ignore — malformed query string
    }

    if (Object.keys(bag).length) touch.params = bag;
    if (referrer) touch.referrer = referrer;
    if (pageUrl) touch.landing_page = pageUrl;
    if (provisional) touch.provisional = true;

    touch.captured_at = new Date().toISOString();
    return touch;
}

function hostOf(url: string | undefined): string | null {
    if (!url) return null;
    try {
        return new URL(url).hostname || null;
    } catch {
        return null;
    }
}

/**
 * True when the site sent the visitor to itself — an internal click, not a new
 * touch. Compared against the landing page rather than window.location, which is
 * the widget's own host when framed.
 */
function isInternalReferrer(touch: RawTouch): boolean {
    const referrerHost = hostOf(touch.referrer);
    if (!referrerHost) return false;
    return referrerHost === hostOf(touch.landing_page);
}

/**
 * Does this page view actually say anything about where the visitor came from?
 *
 * A campaign tag or a click id always does. An external referrer does. Clicking
 * from one page of the site to another does not — and must not be allowed to
 * overwrite a real campaign touch with "(direct)".
 */
function hasNewOrigin(touch: RawTouch): boolean {
    const bag = touch.params;
    if (bag) {
        for (const key in bag) {
            if (Object.prototype.hasOwnProperty.call(bag, key) && ORIGIN_MARKER.test(key)) return true;
        }
    }
    return Boolean(touch.referrer) && !isInternalReferrer(touch);
}

// --- GA cookies --------------------------------------------------------------

function readCookie(predicate: (name: string) => boolean): string | null {
    try {
        const jar = document.cookie ? document.cookie.split(';') : [];
        for (const entry of jar) {
            const separator = entry.indexOf('=');
            if (separator < 0) continue;
            const name = entry.slice(0, separator).trim();
            if (predicate(name)) return entry.slice(separator + 1).trim();
        }
    } catch {
        // ignore — cookies unavailable
    }
    return null;
}

/**
 * `_ga` holds `GA1.1.<client_id>.<first_visit_ts>`; GA reports the last two
 * parts joined as the client id, which is what joins a lead to a GA session.
 */
function readGaClientId(): string | null {
    const value = readCookie((name) => name === '_ga');
    if (!value) return null;
    const parts = value.split('.');
    if (parts.length < 4) return null;
    return `${parts[parts.length - 2]}.${parts[parts.length - 1]}` || null;
}

/**
 * `_ga_<MEASUREMENT_ID>` holds `GS1.1.<session_id>.<session_number>...`.
 * There is one per GA property on the page; any of them identifies the session.
 */
function readGaSessionId(): string | null {
    const value = readCookie((name) => name.indexOf('_ga_') === 0);
    if (!value) return null;
    const parts = value.split('.');
    return parts.length > 2 ? (parts[2] || null) : null;
}

// --- public API --------------------------------------------------------------

/**
 * Site-level kill switch, for operators who want no attribution capture at all
 * without waiting on a config round trip. Set before the bundle loads:
 *
 *   <script>window.__zentriDisableAttribution = true;</script>
 */
function isDisabledGlobally(): boolean {
    try {
        return (window as unknown as Record<string, unknown>).__zentriDisableAttribution === true;
    } catch {
        return false;
    }
}

/**
 * Record where this visitor came from. Call as early as possible on script
 * load — NOT when the chat opens. By the time someone opens a chat they have
 * usually clicked through several pages and the original referrer is long gone.
 *
 * Safe to call more than once; repeat calls on the same page are cheap and the
 * first touch is never overwritten.
 *
 * @param enabled pass false to disable capture for this client.
 */
export function captureAttribution(enabled: boolean = true): void {
    try {
        if (typeof window === 'undefined' || typeof document === 'undefined') return;
        if (!enabled || isDisabledGlobally()) return;

        const current = readCurrentTouch();
        const meaningful = hasNewOrigin(current);

        const storedFirst = readTouch(FIRST_KEY, false);
        // First touch is written once and then left alone — that is what makes
        // it "first". A returning visitor's later campaigns land on `last`.
        //
        // The one exception is a touch we captured blind inside a frame before
        // the host answered: it is a guess, it is being replaced by the host's
        // own account of the same page view, and holding onto it would pin the
        // visitor's origin to whatever the frame could see. A touch that is no
        // longer provisional is never replaced again.
        if (!storedFirst || (storedFirst.provisional && !current.provisional)) {
            writeTouch(FIRST_KEY, current, false, FIRST_TOUCH_TTL_MS);
        }

        // Last touch updates on every genuinely new origin, and once at the
        // start of a session so a direct visit is still recorded as direct.
        const storedLast = readTouch(LAST_KEY, true);
        if (meaningful || !storedLast || (storedLast.provisional && !current.provisional)) {
            writeTouch(LAST_KEY, current, true, FIRST_TOUCH_TTL_MS);
        }

        // Ask the embedding page for its real URL. Deliberately after the
        // capture above, so a visit is recorded even if the host never answers.
        if (current.provisional) requestHostContext();
    } catch {
        // never let attribution break the host page
    }
}

/**
 * The payload to attach to a lead submission, or null when there is nothing
 * worth sending. The backend decides what it means.
 */
export function getAttribution(): AttributionPayload | null {
    try {
        if (typeof window === 'undefined') return null;

        // `provisional` is bookkeeping for the handshake above and means nothing
        // to the backend, so it is dropped rather than sent and ignored.
        const send = (touch: RawTouch | null): RawTouch | null => {
            if (!touch || !touch.provisional) return touch;
            const copy = { ...touch };
            delete copy.provisional;
            return copy;
        };

        const first = send(readTouch(FIRST_KEY, false));
        const last = send(readTouch(LAST_KEY, true));
        const gaClientId = readGaClientId();
        const gaSessionId = readGaSessionId();

        if (!first && !last && !gaClientId && !gaSessionId) return null;

        return {
            first,
            last,
            ga_client_id: gaClientId,
            ga_session_id: gaSessionId,
        };
    } catch {
        return null;
    }
}

/** Forget everything captured. For opt-outs and for tests. */
export function clearAttribution(): void {
    try {
        readStore(false)?.removeItem(FIRST_KEY);
    } catch { /* ignore */ }
    try {
        readStore(true)?.removeItem(LAST_KEY);
    } catch { /* ignore */ }
}
